import React, { useRef, useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface LineNumberedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  errorLines?: number[];
  highlightLines?: number[];
  className?: string;
  disabled?: boolean;
}

export function LineNumberedTextarea({
  value,
  onChange,
  placeholder,
  errorLines = [],
  highlightLines = [],
  className,
  disabled = false,
}: LineNumberedTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const [lineCount, setLineCount] = useState(1);

  // Calculate line count
  useEffect(() => {
    const lines = value.split('\n').length;
    setLineCount(Math.max(lines, 1));
  }, [value]);

  // Sync scroll between textarea and line numbers
  const handleScroll = useCallback(() => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // Scroll to a specific line
  const scrollToLine = useCallback((lineNumber: number) => {
    if (textareaRef.current) {
      const lineHeight = 20; // Approximate line height in pixels
      const targetScroll = (lineNumber - 1) * lineHeight;
      textareaRef.current.scrollTop = targetScroll;
    }
  }, []);

  // Expose scrollToLine via ref (for parent components)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      (textarea as any).scrollToLine = scrollToLine;
    }
  }, [scrollToLine]);

  const isErrorLine = (lineNum: number) => errorLines.includes(lineNum);
  const isHighlightLine = (lineNum: number) => highlightLines.includes(lineNum);

  return (
    <div className={cn('flex border rounded-md bg-muted/30 overflow-hidden', className)}>
      {/* Line numbers column */}
      <div
        ref={lineNumbersRef}
        className="flex-shrink-0 select-none overflow-hidden bg-muted/50 border-r"
        style={{ width: '3rem' }}
      >
        <div className="py-2">
          {Array.from({ length: lineCount }, (_, i) => {
            const lineNum = i + 1;
            const hasError = isErrorLine(lineNum);
            const hasHighlight = isHighlightLine(lineNum);
            
            return (
              <div
                key={lineNum}
                className={cn(
                  'px-2 text-right text-xs font-mono leading-5 cursor-pointer hover:bg-muted/80 transition-colors',
                  hasError && 'bg-destructive/20 text-destructive font-semibold',
                  hasHighlight && !hasError && 'bg-warning/20',
                  !hasError && !hasHighlight && 'text-muted-foreground'
                )}
                onClick={() => scrollToLine(lineNum)}
                title={hasError ? `Error on line ${lineNum}` : `Line ${lineNum}`}
              >
                {lineNum}
              </div>
            );
          })}
        </div>
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={false}
        className={cn(
          'flex-1 resize-none bg-transparent py-2 px-3 text-sm font-mono leading-5 outline-none',
          'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          'min-h-[200px]'
        )}
        style={{
          // Match line numbers height
          lineHeight: '20px',
        }}
      />
    </div>
  );
}

export default LineNumberedTextarea;
